import React, { use, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import PhoneField, { PhoneFieldHandle } from "./PhoneField";
import VStack from "../ui_atoms/VStack";
import parsePhoneNumber from "libphonenumber-js";
import Button, { ButtonProps } from "./Button";
import {
    SEND_WORKER_INVITATION,
    SEND_CLIENT_INVITATION,
    GET_INVITATION,
} from "../graphql/invitation";
import { useI18n } from "../hooks/i18n-hooks";
import Callout from "../ui_atoms/Callout";
import TextField from "./TextField";
import apolloClient from "../configs/graphql.config";
import { AuthManagerContext } from "../contexts/AuthManager";
import { useTheme } from "../hooks/use-theme";
import { Platform, StyleSheet } from "react-native";
import { useCurrentProfile } from "../hooks/use-current-profile";
import { PhoneContactChooser } from "./phone_contact/PhoneContactChooser";
import { GET_CLIENT_WORKERS_AND_INVITATION_WORKERS, GET_CLIENT_USERS } from "../graphql/client";
import Gap from "../ui_atoms/Gap";
import Text from "./Text";
import { useTracking } from "../hooks/tracking";
import { useIntercom } from "../hooks/intercom";
import Label from "../ui_atoms/Label";
import {
    KycStatus,
    ClientProfile,
    ClientStatus,
    ContractSignatureStatus,
    RejectedByType,
    InvitationMode,
    InvitationTarget,
} from "../__generated__/graphql";
import {
    SendInvitationMutation,
    SendClientInvitationMutation,
} from "../__generated__/operations/graphql";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../navigations/RootNavigation";
import { CompositeScreenProps, useNavigation } from "@react-navigation/native";
import AlertBox from "../ui_atoms/AlertBox";
import { BodyMediumText } from "./FormattedText";
import HStack from "../ui_atoms/HStack";
import { InvitationAction, useInvitationHandler } from "../hooks/invitations";
import { useSnackbar } from "../hooks/snackbar";
import { ClientWorkersTabParamList } from "../navigations/client/WorkersNavigation";

type SendInvitationComponentProps = {
    target: InvitationTarget;
    invitationId?: string;
    onSuccess: () => void;
    setButtonProps: (buttonProps: Partial<ButtonProps>[]) => void;
};

type ScreenParams = CompositeScreenProps<
    StackScreenProps<RootStackParamList>,
    StackScreenProps<ClientWorkersTabParamList>
>;

type MutationType = {
    worker: SendInvitationMutation;
    client: SendClientInvitationMutation;
};

const SendInvitationComponent: React.FC<SendInvitationComponentProps> = ({
    target,
    invitationId,
    onSuccess,
    setButtonProps,
}) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const styles = useStyles();
    const { user, refreshUser } = use(AuthManagerContext);
    const currentProfile = useCurrentProfile();
    const [
        sendInvitation,
        { loading: sendInvitationLoading, error: sendInvitationError, reset: sendInvitationReset },
    ] = useMutation<MutationType[typeof target]>(
        target === InvitationTarget.Worker ? SEND_WORKER_INVITATION : SEND_CLIENT_INVITATION,
        {
            refetchQueries: [
                target === InvitationTarget.Worker
                    ? GET_CLIENT_WORKERS_AND_INVITATION_WORKERS
                    : GET_CLIENT_USERS,
            ],
        },
    );
    const {
        data: getInvitationData,
        error: getInvitationError,
        loading: getInvitationLoading,
    } = useQuery(GET_INVITATION, {
        variables: { invitationId: invitationId || "" },
        skip: !invitationId,
    });

    const loading = sendInvitationLoading || getInvitationLoading;
    const error = sendInvitationError || getInvitationError;

    const navigator = useNavigation<ScreenParams["navigation"]>();
    const [sendError, setSendError] = useState<string>();
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const phoneInputRef = useRef<PhoneFieldHandle>(null);
    const [isPhoneValid, setIsPhoneValid] = useState<boolean>(false);
    useEffect(() => {
        setIsPhoneValid(phoneInputRef.current?.isValidNumber(phoneNumber) ?? false);
    }, [phoneNumber]);
    const { trackEvent } = useTracking();
    const { showSnackbar } = useSnackbar();
    const { presentMessageComposer } = useIntercom();

    const onActionSuccess = useCallback(
        (action: InvitationAction) => {
            if (["accept", "reject", "cancel", "resend"].includes(action)) {
                void refreshUser?.();
                showSnackbar({
                    text: i18n.t(`invitation.action_result.${action}.success`),
                    mode: "success",
                });
            }
            navigator.popToTop();
        },
        [navigator, refreshUser, showSnackbar, i18n],
    );

    const {
        onAction,
        processingAction,
        error: procError,
        reset: onActionReset,
    } = useInvitationHandler({ onActionSuccess, target });

    const invitation = getInvitationData?.getInvitation;

    const clearErrors = useCallback(() => {
        if (!invitation) {
            setSendError(undefined);
        }
        sendInvitationReset();
        onActionReset();
    }, [invitation, onActionReset, sendInvitationReset]);

    // Hydrate form fields whenever the parent provides a new `invitation`.
    // Kept as an effect rather than an "adjust state on prop change" render-
    // phase block because the guard there is false on the first render (prev
    // seeded equal to current), which would leave the form empty whenever
    // Apollo's cache-and-network returns the invitation synchronously.
    useEffect(() => {
        if (invitation) {
            setPhoneNumber(invitation.phoneNumber || "");
            setFirstName(invitation.firstName || "");
            setLastName(invitation.lastName || "");
        }
    }, [invitation]);

    const needKyc = useMemo(
        () => user?.kycStatus?.status !== KycStatus.Accepted,
        [user?.kycStatus?.status],
    );

    const isWrongReceiver = useMemo(
        () => invitation && invitation.mode === InvitationMode.Receiver,
        [invitation],
    );

    const clientProfile = useMemo(
        () =>
            currentProfile && "client" in currentProfile ? (currentProfile as ClientProfile) : null,
        [currentProfile],
    );
    const client = clientProfile?.client ?? null;

    // Whether the employer may invite is the back-end's call (`canInviteWorker`), the very rule it
    // checks before sending a worker invitation: accepted under LSE; under WPOne, verified by Lift
    // (the co-employment contract may still be being signed) and not rejected. Colleague invitations
    // follow the same rule, as they always have on this screen.
    const canInvite = !!clientProfile?.canInviteWorker;

    // Lift verifying a WPOne employer — what unlocks inviting — changes no status the app polls and
    // sends no push, so a screen opened locked re-reads the profile once.
    const clientProfileId = clientProfile?.id;
    useEffect(() => {
        if (clientProfileId && !canInvite) {
            void refreshUser?.();
        }
    }, [clientProfileId, canInvite, refreshUser]);

    // Why an employer may not invite (yet): refused, not verified by Lift yet, or — when Lift signed
    // the WPOne co-employment contract before verifying the employer — waiting for its signature.
    const clientWarning = useMemo<{
        messageKey: string;
        messageParams?: Record<string, string>;
        showAssistanceButton?: boolean;
        showClientContract?: boolean;
    } | null>(() => {
        if (!client || canInvite) return null;
        const isWpOne = !client.useLegalEmployer.useLseCct;
        if (client.status === ClientStatus.Rejected) {
            return {
                messageKey:
                    isWpOne && client.rejectedBy === RejectedByType.User
                        ? "invitation.send.client_reject"
                        : "invitation.send.client_refused",
                messageParams: { rejectedReason: client.rejectedReason ?? "" },
                showAssistanceButton: true,
            };
        }
        if (isWpOne && client.status === ClientStatus.Accepted) {
            return client.signatureStatus === ContractSignatureStatus.HandwrittenInProcess
                ? { messageKey: "invitation.send.client_handwritten_in_process" }
                : {
                      messageKey: "invitation.send.client_not_verified_no_lse",
                      showClientContract: true,
                  };
        }
        return { messageKey: "invitation.send.client_not_verified" };
    }, [client, canInvite]);

    const onSendInvitation = useCallback(async () => {
        if (isPhoneValid) {
            phoneInputRef.current?.formatPhoneNumber();
            const result = await sendInvitation({
                variables: { phoneNumber, firstName, lastName },
            });

            const resultData =
                target === InvitationTarget.Worker
                    ? (result.data as SendInvitationMutation)?.sendInvitation
                    : (result.data as SendClientInvitationMutation)?.sendClientInvitation;

            void trackEvent("click_send_invitation");
            if (resultData?.__typename === "SendInvitationSuccess") {
                onSuccess();
            } else if (resultData?.__typename === "SendInvitationError") {
                // A key the back-end added before Lokalise knows it (e.g. `clientNotActive`) would
                // otherwise show i18n-js's "[missing … translation]" placeholder.
                setSendError(
                    i18n.t(`invitation.send.errors.${resultData.messageKey}`, {
                        defaultValue: i18n.t("invitation.send.errors.sendError"),
                    }),
                );
            }
        }
    }, [
        isPhoneValid,
        sendInvitation,
        phoneNumber,
        firstName,
        lastName,
        trackEvent,
        target,
        onSuccess,
        i18n,
    ]);

    useEffect(() => {
        // check if invitation still exists,
        // backend return null if invitation does not exist or is not accessible by the current user
        if (invitationId && !getInvitationLoading && !invitation) {
            void apolloClient.refetchQueries({
                include: [GET_CLIENT_WORKERS_AND_INVITATION_WORKERS],
            });
            // notify user
            showSnackbar({
                text: i18n.t(`invitation.invitation_not_found`),
                mode: "warning",
            });
            if (navigator.canGoBack()) {
                navigator.goBack();
            } else {
                navigator.reset({ index: 0, routes: [{ name: "Home" }] });
            }
        }
    }, [getInvitationLoading, i18n, invitation, invitationId, navigator, showSnackbar]);

    const errorMessage = useMemo(() => {
        if (error) {
            return i18n.t("errors.unknown_error");
        }
        if (sendError) {
            return sendError;
        }
        if (procError) {
            return i18n.t(`invitation.action_result.${processingAction || "default"}.error`);
        }
        return undefined;
    }, [error, sendError, procError, i18n, processingAction]);

    const isSelfInvite =
        isPhoneValid && user?.phoneNumber === phoneNumber && target === InvitationTarget.Client;

    const phoneNumberError = useMemo(
        () => (isSelfInvite ? i18n.t("invitation.send.errors.selfInviteError") : undefined),
        [isSelfInvite, i18n],
    );

    const canSumbit = isPhoneValid && !isSelfInvite;

    useEffect(() => {
        if (!invitationId) {
            setButtonProps([
                {
                    onPress: onSendInvitation,
                    disabled: !canSumbit || loading || needKyc || !canInvite,
                    processing: loading,
                },
            ]);
        } else if (invitation) {
            setButtonProps([
                {
                    onPress: async () => await onAction("cancel", invitation),
                    disabled: loading,
                    processing: !!processingAction,
                },
                {
                    onPress: onSendInvitation,
                    disabled: needKyc || !canInvite,
                    processing: loading,
                },
            ]);
        }
    }, [
        canInvite,
        canSumbit,
        loading,
        onSendInvitation,
        needKyc,
        setButtonProps,
        invitationId,
        invitation,
        processingAction,
        onAction,
    ]);

    return (
        <VStack gap={0} alignment="stretch" style={{ flex: 1 }}>
            {errorMessage && (
                <>
                    <Callout markdown={errorMessage} mode="error" onClose={clearErrors} />
                    <Gap gap={1} />
                </>
            )}
            {isWrongReceiver && (
                <>
                    <Callout mode="error" markdown={i18n.t("invitation.error_bad_recipient")} />
                    <Gap gap={1} />
                </>
            )}
            {needKyc && (
                <>
                    <AlertBox mode="warning">
                        <VStack v={1 / 2} gap={1 / 2} style={{ flex: 1 }} alignment="stretch">
                            <BodyMediumText style={{ color: colors.onWarningBackground }}>
                                {i18n.t(`invitation.send.${target}.kyc_required`)}
                            </BodyMediumText>
                            <HStack fit="center">
                                <Button
                                    style={{ flex: 1 }}
                                    label={i18n.t("invitation.send.launch_kyc")}
                                    onPress={() =>
                                        navigator.navigate("PreIdVerification", {
                                            backOnSkip: true,
                                        })
                                    }
                                />
                            </HStack>
                        </VStack>
                    </AlertBox>
                    <Gap gap={1} />
                </>
            )}
            {!needKyc && clientWarning && (
                <>
                    <AlertBox mode="warning">
                        <VStack v={1 / 2} gap={1 / 2} style={{ flex: 1 }} alignment="stretch">
                            <BodyMediumText style={{ color: colors.onWarningBackground }}>
                                {i18n.t(clientWarning.messageKey, clientWarning.messageParams)}
                            </BodyMediumText>
                            {clientWarning.showClientContract ? (
                                <HStack fit="center">
                                    <Button
                                        style={{ flex: 1 }}
                                        label={i18n.t("invitation.send.show_client_contract")}
                                        onPress={() => {
                                            if (client) {
                                                navigator.navigate("ContractPreviewScreen", {
                                                    contractType: "client_framework",
                                                    contractId: client.id,
                                                });
                                            }
                                        }}
                                    />
                                </HStack>
                            ) : (
                                clientWarning.showAssistanceButton && (
                                    <HStack fit="center">
                                        <Button
                                            style={{ flex: 1 }}
                                            label={i18n.t("invitation.send.contact_support")}
                                            onPress={() => void presentMessageComposer()}
                                        />
                                    </HStack>
                                )
                            )}
                        </VStack>
                    </AlertBox>
                    <Gap gap={1} />
                </>
            )}
            {target === InvitationTarget.Worker && (
                <Text style={styles.text}>{i18n.t("invitation.details.description")}</Text>
            )}
            <Gap gap={1} />
            <TextField
                style={{ flexGrow: 0 }}
                label={i18n.t("invitation.send.firstName")}
                disabled={loading || needKyc || !canInvite || !!invitationId}
                value={firstName}
                onChangeText={setFirstName}
            />
            <Gap gap={1} />
            <TextField
                style={{ flexGrow: 0 }}
                label={i18n.t("invitation.send.lastName")}
                disabled={loading || needKyc || !canInvite || !!invitationId}
                value={lastName}
                onChangeText={setLastName}
            />
            <Gap gap={1} />
            <PhoneField
                style={{ flexGrow: 0 }}
                label={i18n.t("invitation.send.phoneNumber")}
                ref={phoneInputRef}
                onPhoneNumberChanged={setPhoneNumber}
                disabled={loading || needKyc || !canInvite || !!invitationId}
                error={phoneNumberError}
                value={phoneNumber}
            />
            {Platform.OS !== "web" && (
                <>
                    <Gap gap={1 / 2} />
                    <PhoneContactChooser
                        buttonMode="link"
                        buttonSize="small"
                        buttonLabel={i18n.t("invitation.send.my_contacts")}
                        disabled={loading || needKyc || !canInvite}
                        onSelect={(c) => {
                            if (c) {
                                const parsedPhoneNumber = parsePhoneNumber(c.phoneNumber);
                                if (parsedPhoneNumber) {
                                    setPhoneNumber(parsedPhoneNumber.number);
                                    setFirstName(c.firstName || "");
                                    setLastName(c.lastName || "");
                                }
                            }
                        }}
                    />
                </>
            )}
            {canInvite && (
                <>
                    <Gap gap={1} />
                    <Label
                        mode="grey"
                        text={i18n.t(`invitation.send.${target}.explaination`, {
                            clientName: client?.name,
                        })}
                        multiline
                    />
                </>
            )}
        </VStack>
    );
};

export default SendInvitationComponent;

const useStyles = () => {
    const { colors, fonts } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                text: {
                    ...fonts.bodyMedium,
                    color: colors.text,
                },
            }),
        [colors.text, fonts.bodyMedium],
    );
};
